import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { CellValueChangedEvent, ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationHelperService } from '../../../../../../services/confirmation-helper.service';
import { ModalHelperService } from '../../../../../../services/modal-helper.service';
import { CampaignMeasureStore } from '../../../../../../stores/campaign-measure.store';
import { ViewPreferencesService } from '../../../../../../services/view-preferences.service';
import { CesBottomActions } from '../../../../../ui/ces-bottom-actions/ces-bottom-actions.component';
import { CesGridSkeletonComponent } from '../../../../../ui/skeletons/grid-skeleton.component';
import { CesButtonSecondaryComponent } from '../../../../../ui/ces-buttons';
import {
  MeasureColumnToggleComponent,
  MeasureColumnVisibility,
} from '../../../../../measures/measure-column-toggle/measure-column-toggle.component';
import { openCampaignPlanningDialog } from '../planning/open-planning-dialog.util';
import { CampaignPageStore } from '../sub-services/campaign-page.store';
import { CampaignMeasuresGridService, MeasureGridRow, PendingModification } from '../sub-services/campaign-measures-grid.service';
import { CampaignHistoryService, MeasureModificationDetail, CampaignActionContext } from '../sub-services/campaign-history.service';
import { InputDataBulkItem, InputDataStore } from '../../../../../../stores/input-data.store';
import { measurementUpdateTargetsCampaign } from '../../../../../../utils/websocket-match';
import {
  registerMeasurementUpdateRefresh,
  MEASUREMENT_FALLBACK_REFRESH_INTERVAL_MS,
} from '../../../../../../utils/measurement-update-refresh';
import { WebsocketService } from '../../../../../../services/websocket.service';
import { MeasurementUpdatePayload } from '../../../../../../types/websocket.types';
import { FullscreenDirective } from '../../../../../../directives/fullscreen.directive';
import { agGridLocale, noRowsOverlay, syncNoRowsOverlay } from '../../../../../../utils/grid/grid-presets';
import { isKeystrokeClaimedElsewhere } from '../../../../../ui/grid-clipboard/grid-clipboard.utils';

@Component({
  selector: 'app-campaign-measures',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgGridAngular,
    ButtonModule,
    ToastModule,
    TranslocoModule,
    CesBottomActions,
    CesGridSkeletonComponent,
    CesButtonSecondaryComponent,
    FullscreenDirective,
    MeasureColumnToggleComponent,
  ],
  providers: [MessageService, DialogService],
  host: {
    '(document:keydown)': 'onKeyDown($event)',
  },
  templateUrl: './campaign-measures.component.html',
  styleUrl: './campaign-measures.component.scss',
})
export class CesCampaignMeasuresComponent implements OnInit, OnDestroy {
  private static readonly MAX_CONSECUTIVE_ERRORS = 3;
  private static readonly HIGHLIGHT_DURATION_MS = 3_000;

  private readonly messageService = inject(MessageService);
  private readonly dialogService = inject(DialogService);
  private readonly confirmationHelper = inject(ConfirmationHelperService);
  private readonly modalHelper = inject(ModalHelperService);
  private readonly pageStore = inject(CampaignPageStore);
  private readonly campaignMeasureStore = inject(CampaignMeasureStore);
  private readonly inputDataStore = inject(InputDataStore);
  private readonly historyService = inject(CampaignHistoryService);
  private readonly translate = inject(TranslocoService);
  readonly localeText = agGridLocale(this.translate.getActiveLang());
  private readonly ws = inject(WebsocketService);
  private readonly viewPrefs = inject(ViewPreferencesService);
  readonly gridService = inject(CampaignMeasuresGridService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private gridApi: GridApi<MeasureGridRow> | null = null;
  private autoRefreshIntervalId: ReturnType<typeof setInterval> | null = null;
  private highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private consecutiveErrors = 0;

  readonly isLoading = computed(() => !this.pageStore.isMeasuresReady());
  readonly isSaving = signal(false);
  readonly selectedCellCount = signal(0);
  readonly hasActiveFilters = signal(false);
  readonly isCampaignDone = computed(() => this.gridService.isReadOnly());
  private readonly langSignal = signal(this.translate.getActiveLang());

  readonly visibleColumns = signal<MeasureColumnVisibility>(
    this.viewPrefs.getMeasureColumns('campaign') ?? { status: true, calculated: true, verification: true },
  );

  readonly skeletonColumns = computed(() => {
    this.langSignal();
    const visible = this.visibleColumns();
    const cols = [
      this.translate.translate('entities.campaign.grid.measureType'),
      this.translate.translate('entities.campaign.grid.measure'),
      this.translate.translate('entities.campaign.grid.mandatory'),
      this.translate.translate('entities.campaign.grid.location'),
      this.translate.translate('entities.campaign.grid.parameter'),
      this.translate.translate('entities.campaign.grid.valueInput'),
      this.translate.translate('entities.campaign.grid.unitInput'),
    ];
    if (visible.status) cols.push(this.translate.translate('entities.campaign.grid.status'));
    if (visible.calculated) {
      cols.push(this.translate.translate('entities.campaign.grid.calculatedValue'));
      cols.push(this.translate.translate('entities.campaign.grid.unitCalculated'));
    }
    if (visible.verification) {
      cols.push(this.translate.translate('entities.campaign.grid.verificationMin'));
      cols.push(this.translate.translate('entities.campaign.grid.verificationMax'));
    }
    return cols;
  });
  readonly currentCampaignId = this.pageStore.campaignId;

  readonly copyButtonLabel = computed(() => {
    this.langSignal();
    const count = this.selectedCellCount();
    return count > 0
      ? this.translate.translate('common.buttons.copySelection', { count })
      : this.translate.translate('common.buttons.copyAllValues');
  });

  columnDefs: ColDef<MeasureGridRow>[] = this.gridService.createColumnDefs(this.visibleColumns());
  readonly defaultColDef: ColDef = this.gridService.createDefaultColDef();
  readonly gridOptions: GridOptions<MeasureGridRow> = {
    ...this.gridService.createGridOptions(),
    onFilterChanged: () => this.updateFilterState(),
    // Surface the no-rows overlay from the row count so a filter matching nothing reads as
    // "Aucune donnée disponible" rather than a blank grid — consistent with the other grids.
    onModelUpdated: event => syncNoRowsOverlay(event.api),
    onGridPreDestroyed: () => {
      this.gridApi = null;
    },
  };

  constructor() {
    // Start clean: the overlay lives in the root-singleton grid service, so a fresh mount must not
    // inherit a stale "Calcul en cours" flag from a prior instance. The DB-derived status drives the
    // badge until the next live 'pending' frame.
    this.gridService.clearComputing();
    // The error-backoff poll below (startAutoRefresh/stopAutoRefresh) has its own
    // start/stop lifecycle driven by grid state, so only the broadcast subscription
    // is shared; the fallback poll from the util is intentionally left unstarted.
    registerMeasurementUpdateRefresh({
      targets: payload => measurementUpdateTargetsCampaign(payload, this.currentCampaignId()),
      onPending: payload => this.flagMeasuresComputing(payload),
      refresh: () => {
        // Clear BEFORE repaint so the status valueGetter sees an empty overlay, then pull the
        // authoritative statuses; a settle can never leave a row stuck on "Calcul en cours".
        this.gridService.clearComputing();
        this.gridApi?.refreshCells({ force: true, columns: ['status'] });
        void this.silentRefresh();
      },
    });

    effect(() => {
      const hasPendingChanges = this.gridService.hasModifications();
      const loading = this.isLoading();

      if (!hasPendingChanges && !loading) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    });

    effect(() => {
      const visibility = this.visibleColumns();
      this.columnDefs = this.gridService.createColumnDefs(visibility);
      if (this.gridApi) {
        this.gridApi.setGridOption('columnDefs', this.columnDefs);
      }
    });
  }

  ngOnInit(): void {
    this.translate
      .selectTranslation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.langSignal.set(this.translate.getActiveLang());
        this.columnDefs = this.gridService.createColumnDefs(this.visibleColumns());
        if (this.gridApi) {
          this.gridApi.setGridOption('columnDefs', this.columnDefs);
          this.gridApi.setGridOption('overlayNoRowsTemplate', noRowsOverlay(this.translate.translate('common.messages.noData')));
        }
      });
  }

  ngOnDestroy(): void {
    // This component owns the MEASUREMENT_UPDATE subscription and the force-settle timer, so once
    // it is destroyed nothing can settle a pending overlay. Drop it now, or a later terminal frame
    // (arriving off-tab) would leave the root-singleton grid service stranding a stale "Calcul en
    // cours" badge on remount — masking the measure's real status.
    this.gridService.clearComputing();
    this.stopAutoRefresh();
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }
  }

  onGridReady(event: GridReadyEvent<MeasureGridRow>): void {
    this.gridApi = event.api;
    this.revealLinkedMeasure();
  }

  // A graph point links here with ?measureId= so its value can be corrected without hunting for it.
  // The grid only mounts once the rows are in, so they are readable here. One measure spans one row
  // per formula input: aim at the first one that takes a value, since that is where it is typed.
  private revealLinkedMeasure(): void {
    const measureId = Number(this.route.snapshot.queryParams['measureId']);
    if (!Number.isFinite(measureId) || measureId <= 0) return;

    const rows = this.gridService.gridRowData().filter(row => row.measureId === measureId);
    const target = rows.find(row => row.formulaInputId !== 0 && !row.isInactive) ?? rows[0];
    const node = target ? this.gridApi?.getRowNode(target.id) : undefined;
    if (!node) return;

    this.gridApi?.ensureNodeVisible(node, 'middle');
    this.applyHighlight(new Set([node.id as string]));
  }

  onColumnVisibilityChange(visibility: MeasureColumnVisibility): void {
    this.visibleColumns.set(visibility);
    this.viewPrefs.saveMeasureColumns('campaign', visibility);
  }

  onCellSelectionChanged(): void {
    if (!this.gridApi) return;
    const cellRanges = this.gridApi.getCellRanges();
    if (!cellRanges || cellRanges.length === 0) {
      this.selectedCellCount.set(0);
      return;
    }
    let count = 0;
    cellRanges.forEach(range => {
      const startRow = Math.min(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
      const endRow = Math.max(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
      count += (endRow - startRow + 1) * (range.columns?.length ?? 0);
    });
    this.selectedCellCount.set(count);
  }

  clearSelection(): void {
    this.gridApi?.clearCellSelection();
    this.gridApi?.clearFocusedCell();
  }

  private updateFilterState(): void {
    this.hasActiveFilters.set(this.gridApi?.isAnyFilterPresent() ?? false);
  }

  clearAllFilters(): void {
    this.gridApi?.setFilterModel(null);
  }

  onCellValueChanged(event: CellValueChangedEvent<MeasureGridRow>): void {
    if (!event.data || event.colDef.field !== 'parameterValue') return;

    const row = event.data;
    const newValue = event.newValue as number | null;
    const oldValue = row.originalValue;

    if (newValue === oldValue) {
      this.gridService.removeModification(row.id);
    } else {
      const mod: PendingModification = {
        rowId: row.id,
        ...(row.inputDataId !== undefined && { inputDataId: row.inputDataId }),
        formulaInputId: row.formulaInputId,
        campaignId: row.campaignId,
        measureId: row.measureId,
        value: newValue,
        originalValue: oldValue,
        type: row.inputDataId ? 'update' : 'create',
      };
      this.gridService.addModification(mod);
    }

    if (event.node) {
      this.gridApi?.refreshCells({ rowNodes: [event.node], force: true });
    }
  }

  async onSave(): Promise<void> {
    const campaignId = this.currentCampaignId();
    const modifications = Array.from(this.gridService.pendingModifications().values()).filter(m => m.campaignId === campaignId);
    if (modifications.length === 0) return;

    try {
      this.isSaving.set(true);

      await this.inputDataStore.bulkSave(toBulkItems(modifications, new Date().toISOString()));

      if (campaignId) {
        await this.logMeasureModifications(campaignId, modifications);
        this.pageStore.invalidateHistoryCache();
        await this.campaignMeasureStore.getByCampaignIdWithDetails(campaignId, { forceRefresh: true });
      }
      this.gridService.clearAllModifications();

      this.messageService.add({
        severity: 'success',
        summary: this.translate.translate('common.toasts.saveSuccess'),
        detail: this.translate.translate('common.toasts.itemsSaved', { count: modifications.length }),
      });
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.translate('common.toasts.error'),
        detail: this.translate.translate('common.toasts.saveFailed'),
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  private async logMeasureModifications(campaignId: number, modifications: PendingModification[]): Promise<void> {
    const gridRows = this.gridService.gridRowData();
    const rowMap = new Map(gridRows.map(row => [row.id, row]));

    const measureDetails: MeasureModificationDetail[] = modifications.map(mod => {
      const row = rowMap.get(mod.rowId);
      const measureName = row
        ? `${row.measureName} - ${row.parameterName}`
        : `${this.translate.translate('common.labels.measure')} #${mod.measureId}`;
      return {
        measureId: mod.measureId,
        measureName,
        ...(mod.inputDataId !== undefined && { inputDataId: mod.inputDataId }),
        oldValue: mod.originalValue !== null ? String(mod.originalValue) : null,
        newValue: mod.value !== null ? String(mod.value) : null,
        type: mod.type,
      };
    });

    const campaign = this.pageStore.currentCampaign();
    const context: CampaignActionContext = {
      campaignId,
      ...(campaign?.timestamp && { campaignName: this.historyService.formatLogDate(campaign.timestamp) }),
      ...(campaign?.assetId !== undefined && { assetId: campaign.assetId }),
    };

    await this.historyService.logMeasuresUpdate(context, measureDetails);
  }

  async onCancel(): Promise<void> {
    if (
      this.gridService.hasModifications() &&
      !(await this.confirmationHelper.ask(
        'entities.campaign.messages.confirmDiscard',
        'entities.automaticPost.messages.editMode.unsavedTitle',
        undefined,
        'pi pi-exclamation-triangle',
        'common.buttons.discard',
      ))
    ) {
      return;
    }
    this.gridService.clearAllModifications();
    const originalData = this.gridService.gridRowData().map(row => ({ ...row, parameterValue: row.originalValue }));
    this.gridApi?.setGridOption('rowData', originalData);
    this.messageService.add({
      severity: 'info',
      summary: this.translate.translate('common.toasts.info'),
      detail: this.translate.translate('common.toasts.changesCancelled'),
    });
  }

  async onCopyAllValues(): Promise<void> {
    if (!this.gridApi) return;

    const cellRanges = this.gridApi.getCellRanges();
    const hasSelection = cellRanges && cellRanges.length > 0;

    if (hasSelection) {
      this.gridApi.copySelectedRangeToClipboard();
      this.messageService.add({
        severity: 'info',
        summary: this.translate.translate('common.toasts.info'),
        detail: this.translate.translate('common.toasts.cellsCopied', { count: this.selectedCellCount() }),
        life: 2000,
      });
    } else {
      const lines = this.gridService.buildCopyAllLines();

      if (lines.every(line => line === '')) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.translate('common.toasts.warning'),
          detail: this.translate.translate('common.toasts.noValuesToCopy'),
          life: 2000,
        });
        return;
      }

      try {
        await navigator.clipboard.writeText(lines.join('\n'));
        this.messageService.add({
          severity: 'info',
          summary: this.translate.translate('common.toasts.info'),
          detail: this.translate.translate('common.toasts.valuesCopied', { count: lines.length }),
          life: 2000,
        });
      } catch {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('common.toasts.error'),
          detail: this.translate.translate('common.toasts.copyFailed'),
        });
      }
    }
  }

  private startAutoRefresh(): void {
    if (this.autoRefreshIntervalId) return;
    this.autoRefreshIntervalId = setInterval(() => {
      if (!this.ws.isConnected()) {
        void this.silentRefresh();
      }
    }, MEASUREMENT_FALLBACK_REFRESH_INTERVAL_MS);
  }

  private stopAutoRefresh(): void {
    if (this.autoRefreshIntervalId) {
      clearInterval(this.autoRefreshIntervalId);
      this.autoRefreshIntervalId = null;
    }
  }

  private isAnyCellEditing(): boolean {
    if (!this.gridApi) return false;
    const editingCells = this.gridApi.getEditingCells();
    return editingCells && editingCells.length > 0;
  }

  private applyHighlight(updatedRowIds: Set<string>): void {
    if (updatedRowIds.size === 0 && this.gridService.newRowIds().size === 0) return;

    this.gridService.highlightedRowIds.set(updatedRowIds);
    this.gridApi?.redrawRows();

    if (this.highlightTimeoutId) clearTimeout(this.highlightTimeoutId);
    this.highlightTimeoutId = setTimeout(() => {
      this.gridService.newRowIds.set(new Set());
      this.gridService.highlightedRowIds.set(new Set());
      this.gridApi?.redrawRows();
    }, CesCampaignMeasuresComponent.HIGHLIGHT_DURATION_MS);
  }

  // A 'pending' enqueue frame names the recomputing measures (measureIds; singular measureId
  // for measure-scoped frames). Flag their rows Pending and repaint just the status column.
  private flagMeasuresComputing(payload: MeasurementUpdatePayload): void {
    const ids = pendingMeasureIds(payload);
    if (!ids.length) return;
    this.gridService.markComputing(ids);
    this.gridApi?.refreshCells({ force: true, columns: ['status'] });
  }

  private async silentRefresh(): Promise<void> {
    if (this.gridService.hasModifications() || this.isLoading() || this.isAnyCellEditing()) return;

    const campaignId = this.currentCampaignId();
    if (!campaignId) return;

    const beforeRows = this.gridService.gridRowData();

    try {
      await this.campaignMeasureStore.getByCampaignIdWithDetails(campaignId, { silent: true, forceRefresh: true });
      this.consecutiveErrors = 0;

      const { newRowIds, updatedRowIds, removedMeasureNames } = diffRefreshedRows(beforeRows, this.gridService.gridRowData());

      removedMeasureNames.forEach(name => {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.translate('entities.campaign.toasts.measureRemoved'),
          detail: name,
          life: CesCampaignMeasuresComponent.HIGHLIGHT_DURATION_MS,
        });
      });

      if (newRowIds.size > 0) {
        this.gridService.newRowIds.set(newRowIds);
      }
      this.applyHighlight(updatedRowIds);
    } catch {
      this.consecutiveErrors++;
      if (this.consecutiveErrors >= CesCampaignMeasuresComponent.MAX_CONSECUTIVE_ERRORS) {
        this.stopAutoRefresh();
      }
    }
  }

  openPlanningDialog(): void {
    const campaignId = this.currentCampaignId();
    const campaign = this.pageStore.currentCampaign();
    if (!campaignId || !campaign) return;

    openCampaignPlanningDialog(
      { dialogService: this.dialogService, modalHelper: this.modalHelper, translate: this.translate },
      { campaignId, assetId: campaign.assetId, mode: 'measures' },
      () => void this.pageStore.loadMeasuresData(true).catch(() => undefined),
    );
  }

  onKeyDown(event: KeyboardEvent): void {
    const gridApi = this.gridApi;
    if (!gridApi) return;

    // Escape/Delete meant for another layer (editor input, dialog, AG Grid popup) — see helper doc.
    if (isKeystrokeClaimedElsewhere(event)) return;

    const editingCells = gridApi.getEditingCells();
    if (editingCells && editingCells.length > 0) return;

    if (event.key === 'Escape') {
      this.clearSelection();
      return;
    }

    if (event.key !== 'Delete' && event.key !== 'Backspace') return;
    if (this.gridService.isReadOnly()) return;

    const cellRanges = gridApi.getCellRanges();
    if (!cellRanges || cellRanges.length === 0) return;

    event.preventDefault();
    let hasChanges = false;

    cellRanges.forEach(range => {
      const startRow = Math.min(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);
      const endRow = Math.max(range.startRow?.rowIndex ?? 0, range.endRow?.rowIndex ?? 0);

      for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
        const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode?.data) continue;

        range.columns?.forEach(column => {
          if (column.getColId() !== 'parameterValue') return;

          const row = rowNode.data as MeasureGridRow;
          if (row.formulaInputId === 0) return;

          const currentValue = row.parameterValue;
          if (currentValue === null) return;

          row.parameterValue = null;
          hasChanges = true;

          const mod: PendingModification = {
            rowId: row.id,
            ...(row.inputDataId !== undefined && { inputDataId: row.inputDataId }),
            formulaInputId: row.formulaInputId,
            campaignId: row.campaignId,
            measureId: row.measureId,
            value: null,
            originalValue: row.originalValue,
            type: row.inputDataId ? 'update' : 'create',
          };
          this.gridService.addModification(mod);
        });
      }
    });

    if (hasChanges) {
      gridApi.refreshCells({ force: true });
    }
  }
}

export type RefreshRowSnapshot = Pick<MeasureGridRow, 'id' | 'measureId' | 'measureName' | 'calculatedValue' | 'parameterValue'>;

export interface RefreshDiff {
  newRowIds: Set<string>;
  updatedRowIds: Set<string>;
  removedMeasureNames: Set<string>;
}

/** Diffs the grid rows around a silent refresh. A row id encodes
 *  `${campaignMeasureId}-${formulaInputId}`, so a formula reshape (e.g. saisie →
 *  constante) renews every row id of a measure that is still listed. Presence is
 *  therefore tracked per measureId: "removed" means the measure left the campaign,
 *  reshaped rows surface as updates, and only measures absent before count as new. */
export function diffRefreshedRows(before: readonly RefreshRowSnapshot[], after: readonly RefreshRowSnapshot[]): RefreshDiff {
  const beforeById = new Map(before.map(row => [row.id, row]));
  const beforeMeasureIds = new Set(before.map(row => row.measureId));
  const afterMeasureIds = new Set(after.map(row => row.measureId));

  const newRowIds = new Set<string>();
  const updatedRowIds = new Set<string>();

  after.forEach(row => {
    const previous = beforeById.get(row.id);
    if (!previous) {
      (beforeMeasureIds.has(row.measureId) ? updatedRowIds : newRowIds).add(row.id);
    } else if (previous.calculatedValue !== row.calculatedValue || previous.parameterValue !== row.parameterValue) {
      updatedRowIds.add(row.id);
    }
  });

  const removedMeasureNames = new Set<string>();
  before.forEach(row => {
    if (!afterMeasureIds.has(row.measureId)) {
      removedMeasureNames.add(row.measureName);
    }
  });

  return { newRowIds, updatedRowIds, removedMeasureNames };
}

/** A 'pending' enqueue frame names the recomputing measures: measureIds (campaign fan-out) or
 *  the singular measureId (measure-scoped). Returns the ids whose grid rows flash "Calcul en cours". */
export function pendingMeasureIds(payload: MeasurementUpdatePayload): number[] {
  return payload.measureIds ?? (payload.measureId != null ? [payload.measureId] : []);
}

/** Maps pending grid edits to the /input-data/bulk payload. `value` is always shipped:
 *  the bulk contract is tri-state (absent = untouched, explicit null = clears the stored
 *  value), so an operator erasing a cell persists NULL instead of resurrecting the old
 *  value on refresh. The cast bridges Orval's Swagger 2.0 gap (x-nullable is published
 *  but not rendered as `| null`), same idiom as AutomaticMeasureValueUpdatePayload. */
export function toBulkItems(modifications: PendingModification[], timestamp: string): InputDataBulkItem[] {
  return modifications.map(m =>
    m.type === 'update' && m.inputDataId
      ? ({ id: m.inputDataId, value: m.value } as InputDataBulkItem)
      : ({ campaignId: m.campaignId, formulaInputId: m.formulaInputId, value: m.value, timestamp } as InputDataBulkItem),
  );
}
